import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiDownload, FiPrinter, FiSearch, FiPackage, FiRefreshCw, FiExternalLink, FiArrowRight } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { getTransactions } from '../../utils/transactionHelper';
import { formatNumber } from '../../utils/formatters';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

// Stock Transfer report — location→location moves (TranType 'ST', NOT a branch
// transfer). Read-only: queries the existing `transactions` collection by date
// range (covered by the tranTypeCode+docDate index, so no new index needed).

const STATUS_COLORS = {
  Completed: { bg: '#E8F5E9', fg: '#2E7D32' },
  InTransit: { bg: '#FFF8E1', fg: '#B8860B' },
  Draft: { bg: colors.neutral[100], fg: colors.text.secondary },
  Cancelled: { bg: '#FFEBEE', fg: '#C62828' },
};

const formatAmountLocal = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', { minimumFractionDigits: dp, maximumFractionDigits: dp });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StockTransferReport = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('stock-transfer');

  const [rawDocs, setRawDocs] = useState(() => savedMem?.rawDocs ?? []);   // parent transfer docs (this run)
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [fromFilter, setFromFilter] = useState(() => savedMem?.fromFilter ?? 'all');
  const [toFilter, setToFilter] = useState(() => savedMem?.toFilter ?? 'all');
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'all');
  const [search, setSearch] = useState(() => savedMem?.search ?? '');
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);

  // Seed default range to the active financial year — ONLY on a fresh visit.
  // If we restored a saved view (Back navigation), keep the restored dates.
  useEffect(() => {
    if (!savedMem && activeFinancialYear) {
      if (activeFinancialYear.startDate) setDateFrom(activeFinancialYear.startDate);
      if (activeFinancialYear.endDate) setDateTo(activeFinancialYear.endDate);
    }
  }, [activeFinancialYear, savedMem]);

  const loadReport = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const criteria = { tranTypeCode: 'ST' };
      if (dateFrom) criteria.fromDate = dateFrom;
      if (dateTo) criteria.toDate = dateTo;
      const docs = await getTransactions(activeCompany.id, criteria);
      // Keep location-only transfers (exclude branch transfers).
      setRawDocs(docs.filter(d => !d.isBranchTransfer));
      setHasSearched(true);
    } catch (e) {
      console.error('Stock Transfer report load failed:', e);
      setRawDocs([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, dateFrom, dateTo]);

  // Remember this view (filters + loaded rows) once generated, so Back restores
  // it with 0 re-reads. The from/to/status/search filters are display-only over
  // already-loaded data, so we keep the snapshot in sync as they change.
  useEffect(() => {
    if (!hasSearched) return;
    saveMemory({
      rawDocs, hasSearched: true,
      dateFrom, dateTo, fromFilter, toFilter, statusFilter, search
    });
  }, [hasSearched, rawDocs, dateFrom, dateTo, fromFilter, toFilter, statusFilter, search, saveMemory]);

  // Flatten each transfer's line items into detail rows.
  const detailRows = useMemo(() => {
    const rows = [];
    rawDocs.forEach(doc => {
      (doc.lines || []).forEach((line, idx) => {
        rows.push({
          id: `${doc.id}__${idx}`,
          transactionId: doc.id,
          docNumber: doc.docNumber || '-',
          docDate: doc.docDate,
          fromName: doc.fromLocationName || doc.fromLocation || '-',
          toName: doc.toLocationName || doc.toLocation || '-',
          status: doc.status || '-',
          itemCode: line.itemCode || '',
          itemName: line.itemName || line.description || '-',
          quantity: Number(line.quantity) || 0,
          unitOfMeasure: line.unitOfMeasure || line.unitCode || '',
          unitCost: Number(line.unitCost) || 0,
          lineTotal: Number(line.lineTotal) || (Number(line.quantity) || 0) * (Number(line.unitCost) || 0),
        });
      });
    });
    return rows;
  }, [rawDocs]);

  // Filter option lists derived from the loaded data (no extra reads).
  const fromOptions = useMemo(() => [...new Set(detailRows.map(r => r.fromName).filter(Boolean))].sort(), [detailRows]);
  const toOptions = useMemo(() => [...new Set(detailRows.map(r => r.toName).filter(Boolean))].sort(), [detailRows]);
  const statusOptions = useMemo(() => [...new Set(detailRows.map(r => r.status).filter(Boolean))], [detailRows]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return detailRows.filter(r => {
      if (fromFilter !== 'all' && r.fromName !== fromFilter) return false;
      if (toFilter !== 'all' && r.toName !== toFilter) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (term) {
        const hay = `${r.itemCode} ${r.itemName} ${r.docNumber}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [detailRows, fromFilter, toFilter, statusFilter, search]);

  const summary = useMemo(() => {
    const docSet = new Set(filteredRows.map(r => r.transactionId));
    return {
      transfers: docSet.size,
      lines: filteredRows.length,
      totalQty: filteredRows.reduce((s, r) => s + r.quantity, 0),
      totalValue: filteredRows.reduce((s, r) => s + r.lineTotal, 0),
    };
  }, [filteredRows]);

  const handleDocClick = (row) => {
    if (row.transactionId) {
      navigate('/store/stock-transfer', { state: { viewTransactionId: row.transactionId } });
    }
  };

  const handleExport = () => {
    if (filteredRows.length === 0) return;
    const headers = ['Date', 'Doc #', 'From Location', 'To Location', 'Item Code', 'Item', 'Quantity', 'Unit', 'Unit Cost', 'Total Value', 'Status'];
    const lines = filteredRows.map(r => [
      formatDateStr(r.docDate), r.docNumber, r.fromName, r.toName, r.itemCode, r.itemName,
      r.quantity, r.unitOfMeasure, r.unitCost, r.lineTotal, r.status,
    ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-transfer-report_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (filteredRows.length === 0) return;
    const rowsHtml = filteredRows.map(r => `
      <tr>
        <td>${formatDateStr(r.docDate)}</td>
        <td>${r.docNumber}</td>
        <td>${r.fromName}</td>
        <td>${r.toName}</td>
        <td>${r.itemCode ? r.itemCode + ' — ' : ''}${r.itemName}</td>
        <td style="text-align:right">${formatAmountLocal(r.quantity)} ${r.unitOfMeasure}</td>
        <td style="text-align:right">${formatAmountLocal(r.unitCost)}</td>
        <td style="text-align:right">${formatAmountLocal(r.lineTotal)}</td>
        <td>${r.status}</td>
      </tr>`).join('');
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Stock Transfer Report</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#222}
        h2{margin:0 0 4px} .meta{color:#666;font-size:12px;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}
        th{background:#f5f5f5}
      </style></head><body>
      <h2>Stock Transfer Report</h2>
      <div class="meta">${activeCompany?.companyName || ''} · ${dateFrom || 'all'} to ${dateTo || 'all'} · ${summary.transfers} transfers, ${summary.lines} item lines</div>
      <table><thead><tr>
        <th>Date</th><th>Doc #</th><th>From</th><th>To</th><th>Item</th>
        <th style="text-align:right">Qty</th><th style="text-align:right">Unit Cost</th><th style="text-align:right">Total</th><th>Status</th>
      </tr></thead><tbody>${rowsHtml}</tbody></table>
      </body></html>`);
    addPrintBrand(win);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <PageLayout pageTitle="Stock Transfer Report" breadcrumb="Reports / Inventory Reports / Stock Transfer">
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>From Location</Label>
            <Select value={fromFilter} onChange={e => setFromFilter(e.target.value)}>
              <option value="all">All Locations</option>
              {fromOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>To Location</Label>
            <Select value={toFilter} onChange={e => setToFilter(e.target.value)}>
              <option value="all">All Locations</option>
              {toOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Status</Label>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              {statusOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Search Item / Doc</Label>
            <Input type="text" placeholder="Item code, name or doc #" value={search} onChange={e => setSearch(e.target.value)} />
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={filteredRows.length === 0}><FiDownload /> Export CSV</Button>
          <Button onClick={handlePrint} disabled={filteredRows.length === 0}><FiPrinter /> Print</Button>
          <Button $primary onClick={loadReport} disabled={loading}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color={colors.primary.main}><FiRefreshCw /></SummaryIcon>
            <SummaryInfo><SummaryLabel>Transfers</SummaryLabel><SummaryValue>{formatNumber(summary.transfers)}</SummaryValue></SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon $color="#1565C0"><FiPackage /></SummaryIcon>
            <SummaryInfo><SummaryLabel>Item Lines</SummaryLabel><SummaryValue>{formatNumber(summary.lines)}</SummaryValue></SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon $color="#2E7D32"><FiArrowRight /></SummaryIcon>
            <SummaryInfo><SummaryLabel>Total Qty Moved</SummaryLabel><SummaryValue>{formatNumber(summary.totalQty)}</SummaryValue></SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon $color={colors.chart?.accent3 || '#C4A484'}><FiDownload /></SummaryIcon>
            <SummaryInfo><SummaryLabel>Total Value</SummaryLabel><SummaryValue>{formatAmountLocal(summary.totalValue)}</SummaryValue></SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState><EmptyIcon><FiRefreshCw /></EmptyIcon><EmptyText>Loading stock transfers...</EmptyText></EmptyState>
          ) : !hasSearched ? (
            <EmptyState><EmptyIcon><FiPackage /></EmptyIcon><EmptyText>Pick a date range and click "Generate Report" to see stock transfers.</EmptyText></EmptyState>
          ) : filteredRows.length === 0 ? (
            <EmptyState><EmptyIcon><FiPackage /></EmptyIcon><EmptyText>No stock transfers found for the selected filters.</EmptyText></EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Doc #</Th>
                  <Th>From</Th>
                  <Th></Th>
                  <Th>To</Th>
                  <Th>Item</Th>
                  <Th $right>Qty</Th>
                  <Th $right>Unit Cost</Th>
                  <Th $right>Total Value</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRows.map(r => (
                  <Tr key={r.id}>
                    <Td>{formatDateStr(r.docDate)}</Td>
                    <Td>
                      <DocNumber onClick={() => handleDocClick(r)} title="Open transfer">
                        {r.docNumber} <FiExternalLink size={11} />
                      </DocNumber>
                    </Td>
                    <Td><LocTag $from>{r.fromName}</LocTag></Td>
                    <Td style={{ textAlign: 'center', color: colors.text.tertiary }}><FiArrowRight size={13} /></Td>
                    <Td><LocTag>{r.toName}</LocTag></Td>
                    <Td>{r.itemCode && <ItemCodeInline>{r.itemCode}</ItemCodeInline>}{r.itemName}</Td>
                    <AmountCell $right>{formatAmountLocal(r.quantity)}{r.unitOfMeasure ? ` ${r.unitOfMeasure}` : ''}</AmountCell>
                    <AmountCell $right>{formatAmountLocal(r.unitCost)}</AmountCell>
                    <AmountCell $right>{formatAmountLocal(r.lineTotal)}</AmountCell>
                    <Td><StatusTag $s={r.status}>{r.status}</StatusTag></Td>
                  </Tr>
                ))}
                <TotalsRow>
                  <Td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>Totals ({summary.lines} lines)</Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmountLocal(summary.totalQty)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>-</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmountLocal(summary.totalValue)}</AmountCell>
                  <Td></Td>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============
const FiltersCard = styled.div`background:white;border-radius:${colors.borderRadius.lg};box-shadow:${colors.shadows.sm};padding:20px;margin-bottom:20px;`;
const FiltersGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:14px;margin-bottom:16px;`;
const FilterGroup = styled.div`display:flex;flex-direction:column;gap:6px;`;
const Label = styled.label`font-size:11px;font-weight:600;color:${colors.text.secondary};text-transform:uppercase;letter-spacing:0.3px;`;
const Select = styled.select`padding:9px 12px;border:1px solid ${colors.neutral[300]};border-radius:${colors.borderRadius.md};font-size:13px;color:${colors.text.primary};background:white;cursor:pointer;&:focus{outline:none;border-color:${colors.primary.main};box-shadow:0 0 0 3px ${colors.primary.main}15;}`;
const Input = styled.input`padding:9px 12px;border:1px solid ${colors.neutral[300]};border-radius:${colors.borderRadius.md};font-size:13px;color:${colors.text.primary};&:focus{outline:none;border-color:${colors.primary.main};box-shadow:0 0 0 3px ${colors.primary.main}15;}`;
const ButtonGroup = styled.div`display:flex;gap:10px;justify-content:flex-end;`;
const Button = styled.button`
  padding:9px 18px;border:${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};border-radius:${colors.borderRadius.md};font-size:13px;font-weight:500;cursor:pointer;display:flex;align-items:center;gap:7px;transition:all 0.2s;
  ${props => props.$primary ? `background:${colors.primary.main};color:white;&:hover{background:${colors.primary.dark};}` : `background:white;color:${colors.text.primary};&:hover{background:${colors.neutral[100]};border-color:${colors.primary.main};}`}
  &:disabled{opacity:0.5;cursor:not-allowed;}
`;
const SummaryCards = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:16px;margin-bottom:20px;`;
const SummaryCard = styled.div`background:white;border-radius:${colors.borderRadius.lg};box-shadow:${colors.shadows.sm};padding:20px;display:flex;align-items:center;gap:16px;`;
const SummaryIcon = styled.div`width:48px;height:48px;border-radius:${colors.borderRadius.md};background:${props => props.$color || colors.primary.main}15;color:${props => props.$color || colors.primary.main};display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0;`;
const SummaryInfo = styled.div`min-width:0;`;
const SummaryLabel = styled.div`font-size:12px;color:${colors.text.secondary};margin-bottom:4px;white-space:nowrap;`;
const SummaryValue = styled.div`font-size:20px;font-weight:600;color:${colors.text.primary};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
const ContentCard = styled.div`background:white;border-radius:${colors.borderRadius.lg};box-shadow:${colors.shadows.sm};overflow:hidden;`;
const TableContainer = styled.div`overflow-x:auto;`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Thead = styled.thead`background:${colors.neutral[50]};`;
const Th = styled.th`padding:12px 14px;text-align:${props => props.$right ? 'right' : 'left'};font-size:11px;font-weight:600;color:${colors.text.secondary};text-transform:uppercase;letter-spacing:0.5px;border-bottom:2px solid ${colors.neutral[200]};white-space:nowrap;`;
const Tbody = styled.tbody``;
const Tr = styled.tr`border-bottom:1px solid ${colors.neutral[100]};transition:background 0.15s;&:hover{background:${colors.neutral[50]};}`;
const TotalsRow = styled.tr`background:${colors.neutral[100]};border-top:2px solid ${colors.primary.main};td{padding:14px;}&:hover{background:${colors.neutral[100]};}`;
const Td = styled.td`padding:10px 14px;font-size:13px;color:${colors.text.primary};text-align:${props => props.$right ? 'right' : 'left'};`;
const AmountCell = styled(Td)`font-family:'SF Mono','Consolas','Courier New',monospace;font-size:13px;text-align:right;`;
const DocNumber = styled.span`font-weight:500;color:${colors.primary.main};cursor:pointer;display:inline-flex;align-items:center;gap:5px;padding:2px 6px;border-radius:4px;transition:all 0.2s;font-size:13px;&:hover{background:${colors.primary.main}15;text-decoration:underline;}svg{opacity:0.5;}&:hover svg{opacity:1;}`;
const LocTag = styled.span`display:inline-block;padding:2px 8px;border-radius:4px;font-size:12px;font-weight:500;background:${props => props.$from ? '#FFEBEE' : '#E8F5E9'};color:${props => props.$from ? '#C62828' : '#2E7D32'};white-space:nowrap;`;
const ItemCodeInline = styled.span`font-family:'SF Mono','Consolas',monospace;font-size:11px;color:${colors.primary.main};background:${colors.primary.main}10;padding:1px 5px;border-radius:3px;margin-right:6px;white-space:nowrap;`;
const StatusTag = styled.span`display:inline-block;padding:3px 9px;border-radius:12px;font-size:11px;font-weight:600;background:${props => (STATUS_COLORS[props.$s] || STATUS_COLORS.Draft).bg};color:${props => (STATUS_COLORS[props.$s] || STATUS_COLORS.Draft).fg};`;
const EmptyState = styled.div`text-align:center;padding:60px 20px;color:${colors.text.secondary};`;
const EmptyIcon = styled.div`font-size:48px;color:${colors.neutral[300]};margin-bottom:16px;`;
const EmptyText = styled.p`font-size:16px;margin-bottom:8px;`;

export default StockTransferReport;
